import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { FirebaseApp } from 'firebase/app';
import { AppCheck, getToken as getAppCheckToken } from 'firebase/app-check';
import { Auth, getAuth, User } from 'firebase/auth';
import { from, Observable } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { FIREBASE_APP } from '../injections/firebase-app';
import { REPORTING_API_REQUEST } from './reporting-api.service';

@Injectable()
export class ReportingApiAuthInterceptor implements HttpInterceptor {
  constructor(@Inject(FIREBASE_APP) private readonly firebaseApp: FirebaseApp) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!request.context.get(REPORTING_API_REQUEST)) {
      return next.handle(request);
    }

    return from(this.buildHeaders()).pipe(
      mergeMap(headers => next.handle(headers ? request.clone({ setHeaders: headers }) : request)),
    );
  }

  private async buildHeaders(): Promise<Record<string, string> | null> {
    const headers: Record<string, string> = {};
    const authToken = await this.getAuthToken();
    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }

    const appCheckToken = await this.getAppCheckToken();
    if (appCheckToken) {
      headers['X-Firebase-AppCheck'] = appCheckToken;
    }

    return Object.keys(headers).length ? headers : null;
  }

  private async getAuthToken(): Promise<string | null> {
    const auth = getAuth(this.firebaseApp);
    const currentUser = await this.getCurrentUser(auth);

    if (!currentUser) {
      return null;
    }

    return currentUser.getIdToken();
  }

  private getCurrentUser(auth: Auth): Promise<User | null> {
    if (auth.currentUser) {
      return Promise.resolve(auth.currentUser);
    }

    return new Promise(resolve => {
      const unsubscribe = auth.onAuthStateChanged(user => {
        unsubscribe();
        resolve(user);
      });
    });
  }

  private async getAppCheckToken(): Promise<string | null> {
    try {
      const appCheck = this.getInitializedAppCheck();
      if (!appCheck) {
        return null;
      }

      const token = await getAppCheckToken(appCheck, false);
      return token.token ?? null;
    } catch {
      return null;
    }
  }

  private getInitializedAppCheck(): AppCheck | null {
    const appCheckProvider = (this.firebaseApp as any)?._container?.getProvider?.('app-check');
    return appCheckProvider?.getImmediate?.({ optional: true }) ?? null;
  }
}
